// 8F этап 1 (ПРД §13.1) — реестр пунктов задания (ТЗ+ТУ): извлечение и
// ВАЛИДАЦИЯ НАРЕЗКИ владельцем продукта до запуска судьи покрытия.
// Временно живёт над «Сверкой ТЭП»; этап 3 переструктурирует вкладку.
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ListTodo, RefreshCw, EyeOff, Eye, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '@/api/client';

interface ReqItem {
  id: string;
  source: 'ТЗ' | 'ТУ';
  item_no: string | null;
  text: string;
  req_type: 'numeric' | 'scope' | 'document' | 'procedural' | 'general';
  numeric_meta?: { name?: string; op?: string; value?: unknown; unit?: string } | null;
  page?: number | null;
  excluded: boolean;
}

interface ReqResponse {
  has_result: boolean;
  run_id?: string;
  status?: string;
  progress?: { done: number; total: number };
  error?: string | null;
  finished_at?: string | null;
  items: ReqItem[];
}

const TYPE_META: Record<ReqItem['req_type'], { label: string; color: string; bg: string }> = {
  numeric:    { label: 'числовой',    color: '#0369A1', bg: 'rgba(14,165,233,.10)' },
  scope:      { label: 'состав',      color: '#B45309', bg: 'rgba(245,158,11,.12)' },
  document:   { label: 'документ',    color: '#6D28D9', bg: 'rgba(124,58,237,.10)' },
  procedural: { label: 'процедурное', color: '#0E7490', bg: 'rgba(6,182,212,.10)' },
  general:    { label: 'общее',       color: '#6B7280', bg: 'rgba(107,114,128,.10)' },
};

const OP_LABEL: Record<string, string> = { '>=': 'не менее', '<=': 'не более', '=': 'равно' };

export function TaskRequirementsPanel({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  // Сервисный экран настройки — по умолчанию свёрнут (пирамида: заключение
  // и результаты выше, детали настройки — по клику).
  const [open, setOpen] = useState(false);
  const { data } = useQuery<ReqResponse>({
    queryKey: ['task-requirements', projectId],
    queryFn: () => api.get<ReqResponse>(`/projects/${projectId}/task-requirements`),
    refetchInterval: (q) => (q.state.data?.status === 'running' ? 2500 : false),
  });

  useEffect(() => {
    if (data?.status && data.status !== 'running') {
      // финальное состояние — стоп поллинга обеспечивает refetchInterval
    }
  }, [data?.status]);

  const extract = async () => {
    await api.post(`/projects/${projectId}/task-requirements/extract`, {});
    qc.invalidateQueries({ queryKey: ['task-requirements', projectId] });
  };

  const toggle = async (item: ReqItem) => {
    setBusy(item.id);
    try {
      await api.post(`/projects/${projectId}/task-requirements/${item.id}/exclude?excluded=${!item.excluded}`, {});
      qc.invalidateQueries({ queryKey: ['task-requirements', projectId] });
    } finally {
      setBusy(null);
    }
  };

  const items = data?.items ?? [];
  const running = data?.status === 'running';
  const groups: ('ТЗ' | 'ТУ')[] = ['ТЗ', 'ТУ'];

  const excludedCount = items.filter((i) => i.excluded).length;

  return (
    <div className="bg-white rounded-card border border-border" style={{ padding: 24, marginBottom: 20 }}>
      <div className="flex items-start" style={{ gap: 12, marginBottom: open ? 12 : 0 }}>
        <button type="button" onClick={() => setOpen(!open)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 2 }}>
          {open ? <ChevronDown size={18} stroke="#6B7280" /> : <ChevronRight size={18} stroke="#6B7280" />}
        </button>
        <ListTodo size={20} stroke="#6B7280" style={{ marginTop: 2 }} />
        <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => setOpen(!open)}>
          <div className="text-ink" style={{ fontSize: 14, fontWeight: 600 }}>
            Настройка проверки: реестр пунктов задания
            {items.length > 0 && (
              <span className="text-muted" style={{ fontWeight: 400 }}>
                {' '}· {items.length} пунктов{excludedCount ? ` (${excludedCount} исключено)` : ''}
              </span>
            )}
          </div>
          <div className="text-muted" style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 660 }}>
            {open
              ? 'Проверьте нарезку: лишнее (шапки, административные пункты, не-требования) исключите — исключённые пункты не пойдут в проверку соответствия.'
              : 'Разверните, чтобы посмотреть пункты, исключить лишние или переизвлечь реестр.'}
          </div>
        </div>
        <button type="button" onClick={extract} disabled={running}
          style={{ padding: '8px 16px', borderRadius: 20, fontSize: 12.5, fontWeight: 600,
                   background: running ? '#E5E7EB' : '#0A1628', color: running ? '#6B7280' : '#FFF',
                   border: 'none', cursor: running ? 'default' : 'pointer',
                   display: 'flex', alignItems: 'center', gap: 6 }}>
          <RefreshCw size={13} className={running ? 'animate-spin' : undefined} />
          {running ? 'Извлекаю…' : (items.length ? 'Переизвлечь пункты' : 'Извлечь пункты задания')}
        </button>
      </div>

      {running && data?.progress && (
        <div className="text-muted" style={{ fontSize: 12, marginBottom: 10 }}>
          обработано батчей листов: {data.progress.done} из {data.progress.total}
        </div>
      )}
      {data?.status === 'error' && (
        <div style={{ padding: 10, borderRadius: 8, background: 'rgba(239,68,68,.08)', color: '#B91C1C', fontSize: 12.5, marginBottom: 10 }}>
          Извлечение упало: {data.error || 'причина не указана'}
        </div>
      )}

      {open && items.length === 0 && !running && (
        <div className="text-muted text-center" style={{ padding: '28px 16px', border: '1px dashed #E5E7EB', borderRadius: 10, fontSize: 12.5 }}>
          Реестр ещё не извлечён. Нажмите «Извлечь пункты задания» — из vision-листов
          ТЗ и ТУ будут выписаны все пункты требований.
        </div>
      )}

      {open && groups.map((g) => {
        const list = items.filter((i) => i.source === g);
        if (!list.length) return null;
        const active = list.filter((i) => !i.excluded).length;
        return (
          <div key={g} style={{ marginTop: 14 }}>
            <div className="text-ink" style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
              {g} · {active} пунктов{active !== list.length ? ` (+${list.length - active} исключено)` : ''}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {list.map((it) => {
                const t = TYPE_META[it.req_type] ?? TYPE_META.general;
                return (
                  <div key={it.id} className="flex items-start"
                       style={{ gap: 10, padding: '9px 12px', border: '1px solid #F3F4F6', borderRadius: 8,
                                background: it.excluded ? '#FAFAFA' : '#FFF', opacity: it.excluded ? 0.55 : 1 }}>
                    <span className="tabular text-muted" style={{ fontSize: 12, fontWeight: 600, minWidth: 44 }}>
                      {it.item_no || '—'}
                    </span>
                    <span className="rounded-pill" style={{ padding: '1px 8px', fontSize: 10.5, fontWeight: 600,
                                                            background: t.bg, color: t.color, whiteSpace: 'nowrap' }}>
                      {t.label}
                    </span>
                    <div style={{ flex: 1, fontSize: 12.5, lineHeight: 1.5, color: '#1F2937',
                                  textDecoration: it.excluded ? 'line-through' : 'none' }}>
                      {it.text}
                      {it.req_type === 'numeric' && it.numeric_meta?.value != null && (
                        <span className="text-muted" style={{ fontSize: 11, marginLeft: 6 }}>
                          [{it.numeric_meta.name || 'значение'} {OP_LABEL[it.numeric_meta.op || '='] || it.numeric_meta.op} {String(it.numeric_meta.value)}{it.numeric_meta.unit ? ` ${it.numeric_meta.unit}` : ''}]
                        </span>
                      )}
                      {it.page != null && <span className="text-muted" style={{ fontSize: 10.5, marginLeft: 6 }}>стр. {it.page}</span>}
                    </div>
                    <button type="button" onClick={() => toggle(it)} disabled={busy === it.id}
                      title={it.excluded ? 'Вернуть в проверку' : 'Исключить из проверки'}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', padding: 2 }}>
                      {it.excluded ? <Eye size={15} /> : <EyeOff size={15} />}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
